<template>
    <div>
        <div v-if="visible" data-test="security-section">
            <div class="card mb-2">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">Two-Step Verification</h4>
                    </span>
                </div>

                <!-- Native app: setup happens on a computer -->
                <div class="card-body" v-if="native" data-test="security-native">
                    <p class="mb-0">Set up two-step verification on a computer at getmypaystub.com.</p>
                </div>

                <div class="card-body" v-else>
                    <alert-box type="info" v-if="status.impersonating" data-test="security-impersonating">
                        You're signed in as this user, so two-step verification can be viewed but not changed.
                    </alert-box>

                    <!-- Not turned on -->
                    <div v-if="!status.enrolled" data-test="security-off">
                        <p v-if="status.required">Your account requires two-step verification.<template v-if="status.deadline && !status.enforced"> Turn it on by {{ deadline }}.</template></p>
                        <p>Two-step verification adds a second check after your password, so someone who learns your password still can't sign in.</p>
                        <p class="text-muted">You can use an authenticator app, a passkey, or your phone with the GetMyPayStub app.</p>
                        <template v-if="needsSignIn">
                            <alert-box type="warning" data-test="security-stale">
                                To turn on two-step verification, you need to have signed in within the last hour.
                            </alert-box>
                            <button type="button" class="btn btn-primary" data-test="sign-in-again" @click.prevent="signInAgain">
                                Sign in again
                            </button>
                        </template>
                        <button type="button" class="btn btn-primary" data-test="turn-on" v-else-if="canChange" @click.prevent="turnOn">
                            Turn on two-step verification
                        </button>
                    </div>

                    <!-- Turned on -->
                    <div v-else data-test="security-on">
                        <p class="d-flex align-items-center"><i class="far fa-check-circle fa-lg text-primary mr-2" aria-hidden="true"></i>Two-step verification is on.</p>

                        <h5 class="theading mb-1">Trusted phones</h5>
                        <div class="mfa-group mb-3" data-test="list-phones">
                            <div class="mfa-row" v-for="device in status.mobile_devices" :key="device.id">
                                <i class="far fa-mobile mfa-row-icon" aria-hidden="true"></i>
                                <div class="mfa-row-text">
                                    <div>{{ device.label }}</div>
                                    <div class="mfa-meta">{{ phoneMeta(device) }}</div>
                                </div>
                                <button type="button" class="btn btn-link mfa-icon-btn" v-if="canChange" :data-test="`edit-phone-${device.id}`" :aria-label="`Rename ${device.label}`" :title="`Rename ${device.label}`" @click.prevent="editNickname(device)"><i class="far fa-pencil" aria-hidden="true"></i></button>
                                <button type="button" class="btn btn-link mfa-icon-btn" v-if="canChange && canRemove" :data-test="`remove-phone-${device.id}`" :aria-label="`Remove ${device.label}`" :title="`Remove ${device.label}`" @click.prevent="removePhone(device)"><i class="far fa-trash" aria-hidden="true"></i></button>
                            </div>
                            <div class="mfa-row text-muted" v-if="!status.mobile_devices.length">None</div>
                            <button type="button" class="btn btn-link mfa-add" v-if="!native" data-test="get-app" @click.prevent="$refs.sendLinkModal.open()">+ Get the mobile app<template v-if="status.mobile_devices.length"> on another phone</template></button>
                        </div>

                        <h5 class="theading mb-1">Authenticator apps</h5>
                        <div class="mfa-group mb-3" data-test="list-totp">
                            <div class="mfa-row" v-for="d in status.totp_devices" :key="d.id">
                                <i class="far fa-clock mfa-row-icon" aria-hidden="true"></i>
                                <div class="mfa-row-text">
                                    <div>{{ d.name }}</div>
                                    <div class="mfa-meta">{{ lastUsed(d.last_used_on) }}</div>
                                </div>
                                <button type="button" class="btn btn-link mfa-icon-btn" v-if="canChange && canRemove" :data-test="`remove-totp-${d.id}`" :aria-label="`Remove ${d.name}`" :title="`Remove ${d.name}`" @click.prevent="removeTotp(d)"><i class="far fa-trash" aria-hidden="true"></i></button>
                            </div>
                            <div class="mfa-row text-muted" v-if="!status.totp_devices.length">None</div>
                            <button type="button" class="btn btn-link mfa-add" data-test="add-totp" v-if="canChange && status.totp_devices.length < 5" @click.prevent="addTotp">+ Add an authenticator app</button>
                        </div>

                        <h5 class="theading mb-1">Passkeys</h5>
                        <div class="mfa-group mb-3" data-test="list-passkeys">
                            <div class="mfa-row" v-for="pk in status.passkeys" :key="pk.id">
                                <i class="far fa-key mfa-row-icon" aria-hidden="true"></i>
                                <div class="mfa-row-text">
                                    <div>{{ pk.name }}</div>
                                    <div class="mfa-meta">{{ lastUsed(pk.last_used_on) }}</div>
                                </div>
                                <button type="button" class="btn btn-link mfa-icon-btn" v-if="canChange && canRemove" :data-test="`remove-passkey-${pk.id}`" :aria-label="`Remove ${pk.name}`" :title="`Remove ${pk.name}`" @click.prevent="removePasskey(pk)"><i class="far fa-trash" aria-hidden="true"></i></button>
                            </div>
                            <div class="mfa-row text-muted" v-if="!status.passkeys.length">None</div>
                            <button type="button" class="btn btn-link mfa-add" data-test="add-passkey" v-if="canChange && passkeySupported" :disabled="busy" @click.prevent="addNewPasskey">+ Add a passkey</button>
                        </div>

                        <h5 class="theading mb-1">Backup codes</h5>
                        <div class="mfa-group mb-3">
                            <div class="mfa-row">
                                <div class="mfa-row-text" data-test="codes-left">{{ status.backup_codes_left }} {{ status.backup_codes_left == 1 ? 'code' : 'codes' }} left</div>
                                <button type="button" class="btn btn-link btn-sm p-0" data-test="regenerate" v-if="canChange" :disabled="busy" @click.prevent="regenerate">Regenerate</button>
                            </div>
                        </div>

                        <h5 class="theading mb-1">Trusted browsers</h5>
                        <div class="mfa-group mb-3" data-test="list-browsers">
                            <div class="mfa-row" v-for="b in status.trusted_browsers" :key="b.id">
                                <i class="far fa-browser mfa-row-icon" aria-hidden="true"></i>
                                <div class="mfa-row-text">
                                    <div>{{ b.device_string }} <span class="badge badge-secondary" v-if="b.is_this_browser">This browser</span></div>
                                    <div class="mfa-meta">Trusted until {{ $filters.datetime(b.expires_on) }}</div>
                                </div>
                                <button type="button" class="btn btn-link mfa-icon-btn" v-if="canChange" :data-test="`revoke-browser-${b.id}`" :aria-label="`Stop trusting ${b.device_string}`" :title="`Stop trusting ${b.device_string}`" @click.prevent="revokeBrowser(b)"><i class="far fa-trash" aria-hidden="true"></i></button>
                            </div>
                            <div class="mfa-row text-muted" v-if="!status.trusted_browsers.length">None. You can trust a browser for 30 days when you sign in.</div>
                        </div>

                        <div class="pt-2 border-top" v-if="canChange && status.can_disable">
                            <button type="button" class="btn btn-outline-danger" data-test="turn-off" :disabled="busy" @click.prevent="turnOff">Turn off two-step verification</button>
                        </div>
                    </div>
                </div>
            </div>

            <send-link-modal ref="sendLinkModal" />
            <mobile-device-nickname-modal ref="nicknameModal" :device="nicknameDevice" v-if="nicknameDevice" @updated="load" />

            <template v-if="!native && canChange">
                <reverify-modal ref="reverify" :status="status" />
                <mfa-turn-on-modal ref="turnOn" :status="status" :guard="guard" @changed="load" @add-totp="onTurnOnAdd('totp')" @add-passkey="onTurnOnAdd('passkey')" @enrolled="onEnrolled" />
                <totp-setup-modal ref="totp" :guard="guard" @added="load" @close="onSetupClosed" />
                <passkey-setup-modal ref="passkey" :guard="guard" @added="load" @close="onSetupClosed" />
                <backup-codes-modal ref="regenCodes" @close="load" />
            </template>
        </div>
        <!-- Outside v-if="visible": turning MFA off can hide the section -->
        <sign-out-others-prompt ref="signOutOthers" @signed-out="onSignedOutOthers" />
    </div>
</template>

<script>
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import api from '@/api'
import bus from '@/bus'
import { isNativeHost } from '@/native-bridge'
import ReverifyModal from './ReverifyModal'
import MfaTurnOnModal from './MfaTurnOnModal'
import TotpSetupModal from './TotpSetupModal'
import PasskeySetupModal from './PasskeySetupModal'
import BackupCodesModal from './BackupCodesModal'
import SignOutOthersPrompt from './SignOutOthersPrompt'
import MobileDeviceNicknameModal from './MobileDeviceNicknameModal'
import SendLinkModal from '@/components/SendLinkModal'
import { formatDeadline } from '@/mfa-deadline'
import { report, signInAgain } from './mfaHelpers'

const BASE = '/account/mfa'

export default {
    components: {ReverifyModal, MfaTurnOnModal, TotpSetupModal, PasskeySetupModal, BackupCodesModal, SignOutOthersPrompt, MobileDeviceNicknameModal, SendLinkModal},
    props: {
        // from the account page's active-sessions list, which includes this one
        otherSessionCount: {type: Number, default: 0},
        // on the setup page (MfaSetupRequired): after turning on, the session
        // is refused everywhere, so only report it
        setupOnly: {type: Boolean, default: false},
    },
    emits: ['loaded', 'sessions-changed', 'enrolled'],
    data() {
        return {
            status: null,
            staleLogin: false,
            busy: false,
            nicknameDevice: null,
            resumeTurnOn: false,
            native: isNativeHost(),
            passkeySupported: browserSupportsWebAuthn(),
        }
    },
    computed: {
        // `required` alone says nothing: payroll admins can be required while rollout is staff-only
        visible() {
            return !!this.status && (this.status.available || this.status.enrolled)
        },
        canChange() {
            return !this.status.impersonating
        },
        deadline() {
            return formatDeadline(this.status.deadline)
        },
        // Turning on needs a recent sign-in; the server refuses with stale_login
        needsSignIn() {
            return this.canChange && !this.status.enrolled && (!this.status.fresh || this.staleLogin)
        },
        // Mirrors the server guard, which stays authoritative
        canRemove() {
            const s = this.status
            return s.totp_devices.length + s.passkeys.length + s.mobile_devices.length > 1
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        lastUsed(when) {
            return when ? `Last used ${this.$filters.fromNow(when)}` : 'Never used'
        },
        // The model only when a nickname hides it
        phoneMeta(device) {
            return [device.user_label && device.device_model_formatted, this.lastUsed(device.last_used)]
                .filter(Boolean).join(' · ')
        },
        load() {
            return api.get(`${BASE}/`).then((status) => {
                this.status = status
            }).catch((err) => {
                // 404 means MFA is off. Other failures leave what's showing alone.
                if ((err && err.__status__ === 404) || !this.status) {
                    this.status = null
                }
            }).then(() => {
                // The enrolled branch is the only one that lists phones
                this.$emit('loaded', {
                    listsPhones: this.visible && !this.native && this.status.enrolled,
                    devices: (this.status && this.status.mobile_devices) || null,
                })
            })
        },
        signInAgain() {
            signInAgain(this)
        },
        // Run an API call. Re-verifies first when the session isn't fresh, and
        // once more if the server says it went stale.
        async guard(fn) {
            try {
                if (this.status.enrolled && !this.status.fresh) {
                    await this.reverify()
                }
                try {
                    return await fn()
                } catch (err) {
                    if (!err || err.reason !== 'reverify_required') {
                        throw err
                    }
                    await this.reverify()
                    return await fn()
                }
            } catch (err) {
                throw this.refused(err)
            }
        },
        reverify() {
            return this.$refs.reverify.ask().then(() => {
                this.status.fresh = true
            })
        },
        // 403s carry a reason and their own text; show them once
        refused(err) {
            if (err && err.__status__ === 403 && err.reason && !err.__shown__) {
                if (err.reason === 'stale_login') {
                    this.staleLogin = true
                }
                bus.showError(bus.firstError(err))
                err.__shown__ = true
            }
            return err
        },
        // unguarded: for changes that only make the account safer
        act(fn, success, {guarded = true} = {}) {
            this.busy = true
            return (guarded ? this.guard(fn) : fn()).then((resp) => {
                this.busy = false
                if (success) {
                    bus.showInfo(success)
                }
                return this.load().then(() => resp)
            }).catch((err) => {
                this.busy = false
                report(err)
            })
        },
        turnOn() {
            this.$refs.turnOn.start()
        },
        // Turn-on hands over to a setup dialog, then reopens
        onTurnOnAdd(ref) {
            this.resumeTurnOn = true
            this.$refs[ref].open()
        },
        // Re-verify up front, so it doesn't interrupt the dialog
        openSetup(ref) {
            this.resumeTurnOn = false
            this.load().then(() => {
                if (this.status && this.status.enrolled && !this.status.fresh) {
                    return this.reverify()
                }
            }).then(() => {
                this.$refs[ref].open()
            }).catch(report)
        },
        addTotp() {
            this.openSetup('totp')
        },
        addNewPasskey() {
            this.openSetup('passkey')
        },
        onSetupClosed() {
            if (this.resumeTurnOn) {
                this.resumeTurnOn = false
                this.$refs.turnOn.open()
            }
        },
        onEnrolled() {
            if (this.setupOnly) {
                this.$emit('enrolled')
                return
            }
            bus.showInfo('Two-step verification is on.')
            // /me carries mfa_setup, which drives the setup banner
            this.$store.dispatch('GET_USER')
            this.load().then(() => this.offerSignOutOthers())
        },
        // Only when there's something to do. Turn on and off aren't offered
        // while impersonating; the check here is a backstop.
        offerSignOutOthers() {
            if (this.status && this.status.impersonating) {
                return
            }
            const browsers = (this.status && this.status.trusted_browsers) || []
            if (this.otherSessionCount > 0 || browsers.some((b) => !b.is_this_browser)) {
                this.$refs.signOutOthers.ask()
            }
        },
        onSignedOutOthers() {
            this.load()
            this.$emit('sessions-changed')
        },
        removeTotp(d) {
            if (!confirm(`Remove the authenticator app "${d.name}"?`)) {
                return
            }
            this.act(() => api.post(`${BASE}/totp/${d.id}/remove`, {}), 'Authenticator app removed.')
        },
        removePasskey(p) {
            if (!confirm(`Remove the passkey "${p.name}"?`)) {
                return
            }
            this.act(() => api.post(`${BASE}/passkeys/${p.id}/remove`, {}), 'Passkey removed.')
        },
        editNickname(device) {
            this.nicknameDevice = device
            this.$nextTick(() => this.$refs.nicknameModal.open())
        },
        removePhone(device) {
            if (!confirm('Removing this device will turn off 1-button verification on that device.\n\nDo you want to proceed?')) {
                return
            }
            this.act(() => api.post(`/mobile-device/${device.id}/deauthorize`, {}), 'Trusted Device cleared.')
        },
        revokeBrowser(b) {
            if (!confirm('Stop trusting this browser? It will ask for a second step the next time.')) {
                return
            }
            this.act(() => api.post(`${BASE}/trusted-browsers/${b.id}/revoke`, {}), 'Browser revoked.', {guarded: false})
        },
        regenerate() {
            if (!confirm('Your current backup codes will stop working. Make new ones?')) {
                return
            }
            this.busy = true
            this.guard(() => api.post(`${BASE}/backup-codes/regenerate`, {})).then((resp) => {
                this.busy = false
                this.$refs.regenCodes.show(resp.backup_codes)
            }).catch((err) => {
                this.busy = false
                report(err)
            })
        },
        turnOff() {
            if (!confirm('Turn off two-step verification? Your account will be protected by your password and email code only.')) {
                return
            }
            this.act(() => api.post(`${BASE}/disable`, {}), 'Two-step verification is off.').then((resp) => {
                if (resp) {
                    this.offerSignOutOthers()
                }
            })
        },
    },
}
</script>

<style scoped>
.mfa-group {
    border: 1px solid #dddddd;
    border-radius: 6px;
}
.mfa-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
}
.mfa-row + .mfa-row {
    border-top: 1px solid #eeeeee;
}
.mfa-row-icon {
    width: 1.25rem;
    text-align: center;
    color: #525252;
}
.mfa-row-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
}
.mfa-meta {
    font-size: 0.8125rem;
    color: #666666;
}
.mfa-icon-btn {
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    color: #666666;
}
.mfa-icon-btn:hover {
    color: #333333;
}
.mfa-add {
    display: block;
    width: 100%;
    text-align: left;
    border: none;
    border-top: 1px solid #eeeeee;
    border-radius: 0 0 6px 6px;
    padding: 0.625rem 0.75rem 0.625rem 2.75rem;
    font-size: 0.875rem;
}
</style>
